import { AnomalyAnalyzerService } from '@ids-services';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, signal, viewChild } from '@angular/core';
import { BaseComponent } from '@ids-components';
import { finalize } from 'rxjs';
import { PlatformConfigurationFormComponent } from './platform-configuration-form/platform-configuration-form.component';
import { UpdateAnalyzerRulesFormComponent } from './update-analyzer-rules-form/update-analyzer-rules-form.component';
import { LoadingOverlayComponent } from '@microsec/components';

export const STATUSES = {
  STARTING_SOON: 'starting_soon',
  IN_PROGRESS: 'in_progress',
  SUCCESS: 'success',
  FAIL: 'fail',
};

export const FORM_PARAMS = {
  DOWNLOAD_FREQUENCY: 'download_frequency',
  API_TOKEN: 'api_token',
  TIP_URL: 'tip_url',
};

@Component({
  selector: 'app-tip-config',
  templateUrl: './tip-config.component.html',
  styleUrls: ['./tip-config.component.scss'],
  imports: [CommonModule, LoadingOverlayComponent, PlatformConfigurationFormComponent, UpdateAnalyzerRulesFormComponent],
})
export class TipConfigComponent extends BaseComponent implements AfterViewInit, OnDestroy {
  isInitialLoading = signal(true);

  isFirstLoad = signal(true);

  isLoading = signal(false);

  tipConfig = signal<any>(null);

  refreshInterval: number | null = null;

  platformConfigForm = viewChild<PlatformConfigurationFormComponent>('platformConfigForm');

  constructor(public anomalyAnalyzerSrv: AnomalyAnalyzerService) {
    super();
  }

  ngAfterViewInit() {
    this.isInitialLoading.set(false);
    this.getConfigData(false);
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (!!this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  /**
   * Get the tip config data
   * @param isDefault - Whether to get the default config
   * @param isStatusUpdated - Whether the status is updated
   */
  getConfigData(isDefault: any = false, isStatusUpdated = false) {
    if (!isStatusUpdated) {
      this.isLoading.set(true);
    }
    this.anomalyAnalyzerSrv
      .getTipConfig(isDefault)
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
          // Refresh the config data every 30 seconds
          if (!!this.refreshInterval) {
            clearInterval(this.refreshInterval);
          }
          this.refreshInterval = setInterval(() => {
            this.getConfigData(false, true);
          }, 30000);
        }),
      )
      .subscribe({
        next: (response: any) => {
          const previousStatus = this.tipConfig()?.status;
          // Check if the form is first loaded
          if (!!this.isFirstLoad()) {
            this.tipConfig.set(response);
            this.isFirstLoad.set(false);
          } else if (isDefault === false) {
            this.tipConfig.set(response);
            this.platformConfigForm()?.form()?.form?.markAsPristine();
          } else if (!isStatusUpdated) {
            this.platformConfigForm()?.form()?.patchValue(response);
            this.platformConfigForm()?.form()?.form?.markAsDirty();
          }
          if (!!isStatusUpdated && !!previousStatus && previousStatus !== this.tipConfig().status) {
            switch (this.tipConfig()?.status) {
              case STATUSES.SUCCESS: {
                this.showSuccessMessage('Analyzer rules updated successfully');
                break;
              }
              case STATUSES.FAIL: {
                this.showErrorMessage('Analyzer rules failed to update');
                break;
              }
              default: {
                break;
              }
            }
          }
        },
        error: (err: any) => {
          this.tipConfig.set({ ...(this.tipConfig() || {}), status: STATUSES.FAIL });
          this.showErrorMessage(err);
        },
      });
  }
}
